import styled, { css, keyframes } from "styled-components";

const sidebarListRefreshKeyframes = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
    filter: saturate(0.92);
  }

  to {
    opacity: 1;
    transform: translateY(0);
    filter: saturate(1);
  }
`;

const sidebarListRefresh = css`
  animation: ${sidebarListRefreshKeyframes} 180ms cubic-bezier(0.2, 0.8, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const SidebarContainer = styled.aside<{ isHidden?: boolean }>`
  min-width: 300px;
  flex: 40%;
  border-right: 1px solid ${(props) => props.theme.common.borderColor};
  display: flex;
  flex-direction: column;

  @media screen and (min-width: 1000px) and (max-width: 1300px) {
    flex: 35%;

    & ~ div {
      flex: 65%;
    }
  }

  @media screen and (min-width: 1301px) {
    flex: 30%;

    & ~ div {
      flex: 70%;
    }
  }

  .icon {
    color: ${(props) => props.theme.common.headerIconColor};
  }

  @media screen and (max-width: 900px) {
    min-width: 0;
    width: 100%;
    border-right: none;
    border-bottom: 1px solid ${(props) => props.theme.common.borderColor};
    height: 100%;
    position: absolute;
    inset: 0;
    z-index: 2;
    padding-top: env(safe-area-inset-top);

    ${(props) =>
      props.isHidden &&
      css`
        display: none;
      `}
  }
`;

export const Header = styled.header`
  background: ${(props) => props.theme.common.primaryColor};
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: calc(60px + env(safe-area-inset-top));
  padding: calc(10px + env(safe-area-inset-top)) 10px 10px;
  min-height: calc(60px + env(safe-area-inset-top));
`;

export const ImageWrapper = styled.div`
  width: 40px;
  height: 40px;
`;

export const Avatar = styled.img`
  border-radius: 50%;
  height: 100%;
  width: 100%;
  object-fit: cover;
  cursor: pointer;
`;

export const Actions = styled.div`
  margin-right: 20px;

  & > * {
    display: inline-block;
    margin-left: 25px;
    cursor: pointer;
  }
`;

export const ThemeIconContainer = styled.div`
  svg {
    margin-bottom: 2px;
    width: 20px;
    height: 20px;
    fill: ${(props) => props.theme.common.headerIconColor};
  }
`;

export const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: ${(props) => props.theme.common.secondaryColor};
`;

export const ContactContainer = styled.div`
  ${sidebarListRefresh}

  background: ${(props) => props.theme.common.secondaryColor};
  border-top: 1px solid ${(props) => props.theme.common.borderColor};
  position: relative;
  z-index: 1;
`;

export const CampaignBanner = styled.section`
  margin: 0;
  padding: 14px 16px;
  background:
    radial-gradient(circle at top left, rgba(255, 255, 255, 0.2), transparent 40%),
    linear-gradient(135deg, #101caa 0%, #2a38d7 52%, #4b5cff 100%);
  color: #fff;
  box-shadow: 0 14px 28px rgba(42, 56, 215, 0.28);
  direction: rtl;
  position: relative;
  overflow: hidden;
  width: 100%;

  &::after {
    content: "";
    position: absolute;
    inset: auto -34px -34px auto;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
  }

  @media screen and (max-width: 900px) {
    padding: 10px 12px;
    box-shadow: 0 10px 20px rgba(42, 56, 215, 0.2);
  }
`;

export const CampaignHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  position: relative;
  z-index: 1;
`;

export const CampaignLogo = styled.img`
  width: 118px;
  height: auto;
  display: block;
  flex-shrink: 0;
  filter: brightness(0) invert(1);

  @media screen and (max-width: 900px) {
    width: 82px;
  }
`;

export const CampaignTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  line-height: 1.25;
  font-weight: 700;
  position: relative;
  z-index: 1;

  @media screen and (max-width: 900px) {
    font-size: 0.88rem;
  }
`;

export const CampaignText = styled.p`
  margin: 6px 0 0;
  font-size: 0.82rem;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.92);
  position: relative;
  z-index: 1;
`;

export const CampaignActions = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 10px;
  position: relative;
  z-index: 1;

  @media screen and (max-width: 900px) {
    margin-top: 8px;
  }
`;

export const CampaignLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: fit-content;
  flex: 0 0 auto;
  min-height: 34px;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.28);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1;
  text-decoration: none;
  text-decoration-line: none;
  backdrop-filter: blur(8px);
  transform: none;
  box-shadow: none;
  transition:
    background-color 140ms ease,
    border-color 140ms ease;

  &:visited {
    color: #fff;
    text-decoration: none;
    text-decoration-line: none;
  }

  &:hover {
    transform: none;
    background: rgba(255, 255, 255, 0.22);
    border-color: rgba(255, 255, 255, 0.4);
    box-shadow: none;
    color: #fff;
    font-size: 0.8rem;
    font-weight: 700;
    line-height: 1;
    text-decoration: none;
    text-decoration-line: none;
  }

  &:focus,
  &:focus-visible,
  &:active {
    color: #fff;
    text-decoration: none;
    text-decoration-line: none;
  }

  @media screen and (max-width: 900px) {
    width: 100%;
    min-height: 30px;
    padding: 7px 12px;
    font-size: 0.76rem;
  }
`;

export const SidebarFilterWrap = styled.div`
  background: ${(props) => props.theme.common.secondaryColor};
  padding: 8px 10px 10px;
  overflow: visible;
  direction: ltr;
  position: relative;
  z-index: 5;
`;

export const SidebarFilterHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 8px;
  direction: rtl;
`;

export const SidebarFilterTitle = styled.h3`
  margin: 0;
  font-size: 0.78rem;
  font-weight: 600;
  color: ${(props) => props.theme.common.subHeadingColor};
`;

export const SidebarFilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding-bottom: 6px;
  direction: ltr;

  @media screen and (max-width: 900px) {
    gap: 8px;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 2px;
    touch-action: pan-x;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

export const SidebarFilterChip = styled.div<{ $isSelected: boolean }>`
  border: 1px solid ${(props) => props.theme.common.borderColor};
  border-radius: 999px;
  padding: 8px 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${(props) => props.theme.common.secondaryColor};
  color: ${(props) => props.theme.common.mainHeadingColor};
  font-size: 0.78rem;
  font-weight: 500;
  direction: rtl;
  position: relative;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;
  z-index: 1;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  transition:
    background-color 140ms ease,
    border-color 140ms ease,
    box-shadow 140ms ease,
    transform 140ms ease;

  ${(props) =>
    props.$isSelected &&
    css`
      background: ${props.theme.common.tertiaryColor};
      border-color: ${props.theme.common.tertiaryColor};
      color: #fff;
      box-shadow: 0 10px 24px rgba(17, 27, 33, 0.14);
    `}

  &:hover {
    background: ${(props) =>
      props.$isSelected ? props.theme.common.tertiaryColor : "rgba(0, 0, 0, 0.035)"};
    transform: translateY(-1px);
    z-index: 8;
  }

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.common.readIconColor};
    outline-offset: 2px;
    z-index: 8;
  }

  &:hover > span[data-sidebar-tooltip],
  &:focus-within > span[data-sidebar-tooltip] {
    opacity: 1;
    transform: translate(-50%, 0);
    pointer-events: auto;

    @media screen and (max-width: 900px) {
      transform: translateY(0);
    }
  }

  &:focus-within {
    z-index: 8;
  }

  @media screen and (max-width: 900px) {
    flex: 0 0 auto;
    max-width: none;
    padding: 7px 10px;
    gap: 6px;
    font-size: 0.73rem;
    line-height: 1.1;
    white-space: nowrap;
    align-items: center;
  }
`;

export const SidebarFilterChipSymbol = styled.span`
  min-width: 22px;
  height: 22px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.06);
  font-size: 0.88rem;
  line-height: 1;

  @media screen and (max-width: 900px) {
    min-width: 18px;
    height: 18px;
    font-size: 0.75rem;
  }
`;

export const SidebarFilterChipInfo = styled.button`
  width: 18px;
  height: 18px;
  border: none;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: rgba(0, 0, 0, 0.08);
  color: ${(props) => props.theme.common.subHeadingColor};
  font-size: 0.67rem;
  font-weight: 700;
  flex-shrink: 0;
  cursor: help;
`;

export const SidebarFilterTooltip = styled.span.attrs({ "data-sidebar-tooltip": true })`
  position: absolute;
  left: 50%;
  top: calc(100% + 8px);
  transform: translate(-50%, 6px);
  min-width: 180px;
  max-width: min(72vw, 250px);
  padding: 8px 10px;
  border-radius: 12px;
  background: rgba(17, 27, 33, 0.96);
  color: #fff;
  font-size: 0.72rem;
  line-height: 1.45;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  text-align: right;
  box-shadow: 0 16px 34px rgba(17, 27, 33, 0.24);
  z-index: 20;
  opacity: 0;
  pointer-events: none;
  transition:
    opacity 140ms ease,
    transform 140ms ease;

  &::before {
    content: "";
    position: absolute;
    bottom: 100%;
    left: calc(50% - 6px);
    border-width: 0 6px 6px 6px;
    border-style: solid;
    border-color: transparent transparent rgba(17, 27, 33, 0.96) transparent;
  }

  @media screen and (max-width: 900px) {
    position: fixed;
    left: 12px;
    right: 12px;
    top: auto;
    bottom: calc(12px + env(safe-area-inset-bottom));
    min-width: 0;
    max-width: none;
    transform: translateY(6px);
    text-align: right;

    &::before {
      display: none;
    }
  }
`;

export const MobileFilterInfoPanel = styled.div<{ $arrowLeft: number; $left: number; $width: number }>`
  display: none;

  @media screen and (max-width: 900px) {
    display: block;
    position: absolute;
    left: ${(props) => `${props.$left}px`};
    top: calc(100% - 2px);
    width: ${(props) => `${props.$width}px`};
    padding: 8px 10px;
    border-radius: 10px;
    background: rgba(17, 27, 33, 0.92);
    color: #fff;
    font-size: 0.74rem;
    line-height: 1.45;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
    text-align: right;
    direction: rtl;
    z-index: 30;
    box-shadow: 0 16px 30px rgba(0, 0, 0, 0.24);

    &::before {
      content: "";
      position: absolute;
      bottom: 100%;
      left: ${(props) => `${props.$arrowLeft}px`};
      border-width: 0 6px 6px 6px;
      border-style: solid;
      border-color: transparent transparent rgba(17, 27, 33, 0.92) transparent;
    }
  }
`;
